import type { LanguageCode } from "config/languages";
import get from "lodash/get";
import { useContext } from "react";
import type { DatasourceProps } from "types/datasource";
import type { ServerSideStoryblokPages } from "types/storyblok";
import type { PageName } from "utils/getPageUrl/types";
import type { TrustpilotReview, TrustpilotBusinessUnit } from "services/trustpilot/trustpilot";
import { useExtendedRouter } from "hooks";
import { DataContext } from "./DataContext";
import type { CMSLanguagesData, ReferralProgramConfig } from "./hooks.d";

export const useReviews = () => get(useContext(DataContext), "reviews", []) as TrustpilotReview[];

export const useTrustpilotInfo = () => get(useContext(DataContext), "trustpilotInfo", {}) as TrustpilotBusinessUnit;

export const useReferralProgramConfig = () =>
  get(useContext(DataContext), "referralProgramConfig", {}) as ReferralProgramConfig;

export const usePageName = () => get(useContext(DataContext), "pageName", "") as PageName;

export const useCMSLanguageData = () => get(useContext(DataContext), "cmsLanguagesData", []) as CMSLanguagesData[];

export const useServerSideStoryblokPages = () =>
  get(useContext(DataContext), "serverSideStoryblokPages", {}) as ServerSideStoryblokPages;

export const useServerSideStoryblokOtaPages = () =>
  get(useContext(DataContext), "serverSideStoryblokOtaPages", {}) as ServerSideStoryblokPages;

export const useWhiteListDomains = () => get(useContext(DataContext), "whiteListDomains", []) as DatasourceProps[];

export const useNofollowUrls = () => get(useContext(DataContext), "nofollowUrls", []) as string[];

export const useGoogleOptimizeExperimentId = (
  experimentName: string,
  supportedUrls: Array<{ language: LanguageCode; slug?: string }>
) => {
  const { query, isHomepage } = useExtendedRouter();
  const queryLanguage = query?.language as LanguageCode;
  const querySlug = query?.slug?.[0] as string;

  const isSupportedUrl = supportedUrls.some(
    ({ language, slug }) =>
      (queryLanguage === language && typeof slug === "undefined") ||
      (querySlug === slug && queryLanguage === language) ||
      (slug === "" && isHomepage && queryLanguage === language)
  );

  const experimentIds = get(useContext(DataContext), "googleOptimizeExperimentIds", []) as DatasourceProps[];

  if (!isSupportedUrl) return null;
  const selectedExperiment = experimentIds.find((experiment) => experiment.name === experimentName);

  if (!selectedExperiment) return null;
  const { value, dimension_value: dimensionValue } = selectedExperiment;

  return dimensionValue ?? value;
};
